import { useContext, useEffect, useState } from 'react';

import { SimulationContext, type SubSimulation } from 'contexts/SimulationContext';

import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { Grid, TextInput } from 'components/lib';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import {
  MAX_SOLAR_TIME_UNITS_TOTAL,
  SOLAR_DAYS_MAX,
  SOLAR_DAYS_MIN,
  SOLAR_HOURS_MAX,
  SOLAR_HOURS_MIN,
  SOLAR_MINUTES_MAX,
  SOLAR_MINUTES_MIN,
  SOLAR_MONTHS_MAX,
  SOLAR_MONTHS_MIN,
} from 'constants/simulation';
import { DeleteIcon } from 'components/icons';
import { UMButton, UMInputNumber } from '@urbanmetrix/um-kit';

const calculateSolarTimeUnitsTotal = (months: number, days: number, hours: number, minutes: number) => {
  if (months === 0 || days === 0 || hours === 0 || minutes === 0) {
    return Number.POSITIVE_INFINITY;
  }

  return (12 / months) * (30 / days) * (18 / hours) * (60 / minutes);
};

const getSolarFieldRangeError = (value: number, min: number, max: number) => {
  if (!Number.isFinite(value) || value < min || value > max) {
    return `Please enter a number between ${min} and ${max}`;
  }

  return null;
};

const getSolarFieldErrorMessage = (label: string, value: number, min: number, max: number) => {
  const rangeError = getSolarFieldRangeError(value, min, max);
  if (!rangeError) {
    return null;
  }

  return `${label} must be between ${min} and ${max}.`;
};

const Solar = () => {
  const { subSimulations, setSubSimulations, setSubSimulationName, setSubSimulationParams, defaultSolar } =
    useContext(SimulationContext);
  const getTimeUnitDefaults = (frameDivision: string | undefined) => {
    if (frameDivision === 'MONTHLY') {
      return { months: 1, days: 5, hours: 1, minutes: 10 };
    }
    if (frameDivision === 'HOURLY') {
      return { months: 1, days: 5, hours: 1, minutes: 10 };
    }
    return { months: 1, days: 2, hours: 1, minutes: 30 };
  };
  const getDefaultsForSubSimulation = (subSimulation: SubSimulation) =>
    getTimeUnitDefaults(subSimulation?.params?.frameDivision as string | undefined);
  const applyTimeUnitDefaults = (id: number, frameDivision: string) => {
    const defaults = getTimeUnitDefaults(frameDivision);
    setSubSimulationParams(id, {
      raysEveryNMonths: defaults.months,
      raysEveryNDays: defaults.days,
      raysEveryNHours: defaults.hours,
      raysEveryNMinutes: defaults.minutes,
    });
  };

  const handleNameChange = (newName: string, id: number) => {
    setSubSimulationName(newName, id);
  };
  const toInputValue = (value: unknown): number | string | null => {
    if (value == null) return null;
    if (typeof value === 'number' || typeof value === 'string') return value;
    return null;
  };
  const handleInputsChange = (name: string | undefined, value: unknown, id: number) => {
    if (!name) return;
    setSubSimulationParams(id, { [name]: value });
  };

  const [newSimulationId, setNewSimulationId] = useState<number | null>(null);
  const [newSimulationName, setNewSimulationName] = useState<string | null>(null);

  const addSunSimulation = () => {
    const newId =
      subSimulations.length > 0 ? Math.max(...subSimulations.map((subSimulation) => subSimulation.id)) + 1 : 0;
    const newSubSimulation = defaultSolar(newId);
    setSubSimulations([...subSimulations, newSubSimulation]);
    setNewSimulationId(newId);
    setNewSimulationName(newSubSimulation.name ?? null);
  };

  // Effect to handle name change after the new simulation is added
  useEffect(() => {
    if (newSimulationId !== null && newSimulationName !== null) {
      handleNameChange(newSimulationName, newSimulationId);
      setNewSimulationId(null);
      setNewSimulationName(null);
    }
  }, [newSimulationId, newSimulationName]);
  const removeBlock = (id: number) => {
    setSubSimulations(subSimulations.filter((block) => block.id !== id));
  };

  return (
    <>
      {subSimulations
        .filter((subSim) => subSim.simType === 'SUN')
        .map((subSimulation, index) => {
          const raysEveryNMonths = Number(subSimulation.params?.raysEveryNMonths ?? 0);
          const raysEveryNDays = Number(subSimulation.params?.raysEveryNDays ?? 0);
          const raysEveryNHours = Number(subSimulation.params?.raysEveryNHours ?? 0);
          const raysEveryNMinutes = Number(subSimulation.params?.raysEveryNMinutes ?? 0);
          const monthsError = getSolarFieldErrorMessage(
            'Every n months',
            raysEveryNMonths,
            SOLAR_MONTHS_MIN,
            SOLAR_MONTHS_MAX
          );
          const daysError = getSolarFieldErrorMessage('Every n days', raysEveryNDays, SOLAR_DAYS_MIN, SOLAR_DAYS_MAX);
          const hoursError = getSolarFieldErrorMessage(
            'Every n hours',
            raysEveryNHours,
            SOLAR_HOURS_MIN,
            SOLAR_HOURS_MAX
          );
          const minutesError = getSolarFieldErrorMessage(
            'Every n minutes',
            raysEveryNMinutes,
            SOLAR_MINUTES_MIN,
            SOLAR_MINUTES_MAX
          );
          const solarTimeUnitsTotal = calculateSolarTimeUnitsTotal(
            raysEveryNMonths,
            raysEveryNDays,
            raysEveryNHours,
            raysEveryNMinutes
          );
          const allSolarFieldsWithinOwnRange = !monthsError && !daysError && !hoursError && !minutesError;
          const isSolarTimeUnitsTotalValid =
            allSolarFieldsWithinOwnRange &&
            Number.isFinite(solarTimeUnitsTotal) &&
            solarTimeUnitsTotal < MAX_SOLAR_TIME_UNITS_TOTAL;
          // Only flag the total as the culprit when every field is individually valid on its own -
          // otherwise an out-of-range field would incorrectly mark the other, valid, fields as invalid too.
          const isSolarTimeUnitsTotalExceeded = allSolarFieldsWithinOwnRange && !isSolarTimeUnitsTotalValid;
          const solarTotalErrorMessage = `The total number of solar time units must be lower than ${MAX_SOLAR_TIME_UNITS_TOTAL}. Increase months, days, hours, or minutes.`;
          const monthsFieldErrorMessage =
            monthsError ?? (isSolarTimeUnitsTotalExceeded ? solarTotalErrorMessage : undefined);
          const daysFieldErrorMessage =
            daysError ?? (isSolarTimeUnitsTotalExceeded ? solarTotalErrorMessage : undefined);
          const hoursFieldErrorMessage =
            hoursError ?? (isSolarTimeUnitsTotalExceeded ? solarTotalErrorMessage : undefined);
          const minutesFieldErrorMessage =
            minutesError ?? (isSolarTimeUnitsTotalExceeded ? solarTotalErrorMessage : undefined);

          return (
            <div key={index}>
              <Grid cols="3">
                <TextInput
                  label="Simulation Name"
                  name={`name`}
                  value={subSimulation?.name || ''}
                  onChange={(_name, value) => handleNameChange(value, subSimulation.id)}
                />
                <br />

                <RadioGroup
                  row
                  value={subSimulation.params?.frameDivision ?? 'YEARLY'}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleNameChange(e.target.name, subSimulation.id);
                    handleInputsChange('frameDivision', value, subSimulation.id);
                    applyTimeUnitDefaults(subSimulation.id, value);
                  }}
                >
                  <FormControlLabel
                    value="YEARLY"
                    control={<Radio />}
                    name="Yearly Sun"
                    className="radio-row"
                    label={
                      <>
                        Yearly
                        <CustomTooltip title={'Result as 1 yearly frame.'} />
                      </>
                    }
                  />
                  <FormControlLabel
                    value="MONTHLY"
                    name="Monthly Sun"
                    control={<Radio />}
                    className="radio-row"
                    label={
                      <>
                        Monthly
                        <CustomTooltip title={'Result as 12 monthly frames.'} />
                      </>
                    }
                  />
                  <FormControlLabel
                    value="HOURLY"
                    name="Hourly Sun"
                    control={<Radio />}
                    className="radio-row"
                    label={
                      <>
                        Hourly
                        <CustomTooltip title={'Result as 24 hourly frames.'} />
                      </>
                    }
                  />
                </RadioGroup>
                <div style={{ textAlign: 'right' }}>
                  <IconButton size="large" color="error" onClick={() => removeBlock(subSimulation.id)}>
                    <DeleteIcon />
                  </IconButton>
                </div>
              </Grid>

              <Grid cols="4">
                {(() => {
                  const timeUnitDefaults = getDefaultsForSubSimulation(subSimulation);
                  return (
                    <>
                      <UMInputNumber
                        label={
                          <span className="grid-inline-label-with-info">
                            <span>Every n months</span>
                            <CustomTooltip title={'Ideally a divisor of 12'} />
                          </span>
                        }
                        min={SOLAR_MONTHS_MIN}
                        max={SOLAR_MONTHS_MAX}
                        width={84}
                        maxErrorMessageWidth={200}
                        name="raysEveryNMonths"
                        value={toInputValue(subSimulation.params?.raysEveryNMonths)}
                        onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                        defaultValue={timeUnitDefaults.months}
                        errorMessage={monthsFieldErrorMessage}
                        valid={!monthsError && !isSolarTimeUnitsTotalExceeded}
                      />
                      <UMInputNumber
                        label={
                          <span className="grid-inline-label-with-info">
                            <span>Every n days</span>
                            <CustomTooltip title={'Ideally a divisor of 30'} />
                          </span>
                        }
                        min={SOLAR_DAYS_MIN}
                        max={SOLAR_DAYS_MAX}
                        width={84}
                        maxErrorMessageWidth={200}
                        name="raysEveryNDays"
                        value={toInputValue(subSimulation.params?.raysEveryNDays)}
                        onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                        defaultValue={timeUnitDefaults.days}
                        errorMessage={daysFieldErrorMessage}
                        valid={!daysError && !isSolarTimeUnitsTotalExceeded}
                      />
                      <UMInputNumber
                        label={
                          <span className="grid-inline-label-with-info">
                            <span>Every n hours</span>
                            <CustomTooltip title={'Ideally a divisor of 24'} />
                          </span>
                        }
                        min={SOLAR_HOURS_MIN}
                        max={SOLAR_HOURS_MAX}
                        width={84}
                        maxErrorMessageWidth={200}
                        name="raysEveryNHours"
                        value={toInputValue(subSimulation.params?.raysEveryNHours)}
                        onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                        defaultValue={timeUnitDefaults.hours}
                        errorMessage={hoursFieldErrorMessage}
                        valid={!hoursError && !isSolarTimeUnitsTotalExceeded}
                      />
                      <UMInputNumber
                        label={
                          <span className="grid-inline-label-with-info">
                            <span>Every n minutes</span>
                            <CustomTooltip title={'Ideally a divisor of 60'} />
                          </span>
                        }
                        min={SOLAR_MINUTES_MIN}
                        max={SOLAR_MINUTES_MAX}
                        width={84}
                        maxErrorMessageWidth={200}
                        name="raysEveryNMinutes"
                        value={toInputValue(subSimulation.params?.raysEveryNMinutes)}
                        onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                        defaultValue={timeUnitDefaults.minutes}
                        errorMessage={minutesFieldErrorMessage}
                        valid={!minutesError && !isSolarTimeUnitsTotalExceeded}
                      />
                    </>
                  );
                })()}
              </Grid>
              <br />
              <Divider />
              <br />
            </div>
          );
        })}

      <UMButton text="Add sim" onClick={addSunSimulation} className="glass-btn-1" />
    </>
  );
};

export default Solar;
